/**
 * GenerateKeyModal — Modal to create a new API key with name, scoped permissions,
 * rate limit, optional expiry, and optional webhook configuration.
 *
 * Props:
 *   open        : boolean
 *   onCancel    : () => void
 *   onSuccess   : (response) => void  — called with the generate response
 *   wabaReady   : boolean — whether WABA embedded signup is complete
 */
import React, { useState, useEffect } from 'react';
import {
  Modal, Input, Button, Checkbox, message, Typography, InputNumber,
  DatePicker, Switch, Alert, Spin, Divider, Tooltip, Tag,
} from 'antd';
import {
  KeyOutlined, WarningOutlined, GlobalOutlined, InfoCircleOutlined,
  SafetyOutlined, PlusOutlined as PlusIcon,
} from '@ant-design/icons';
import ApiKeyService from '../../../../../services/apiKeyService';

const { Text } = Typography;

// Fallback scope groups if /scopes endpoint is unavailable
const FALLBACK_SCOPE_GROUPS = [
  {
    label: 'Contacts & Addressbooks',
    scopes: [
      { value: 'contacts:read', desc: 'Read contacts and addressbook data' },
      { value: 'contacts:write', desc: 'Create, update, delete contacts and addressbooks' },
    ],
  },
  {
    label: 'WhatsApp Templates',
    requiresWaba: true,
    scopes: [
      { value: 'templates:read', desc: 'Read WhatsApp templates' },
      { value: 'templates:write', desc: 'Create, update, delete WhatsApp templates' },
    ],
  },
  {
    label: 'Email Templates',
    scopes: [
      { value: 'email_templates:read', desc: 'Read email templates' },
      { value: 'email_templates:write', desc: 'Create, update, delete email templates' },
    ],
  },
  {
    label: 'WhatsApp Campaigns',
    requiresWaba: true,
    scopes: [
      { value: 'campaigns:read', desc: 'Read WhatsApp campaign data and analytics' },
      { value: 'campaigns:write', desc: 'Create and update WhatsApp campaigns' },
      { value: 'campaigns:execute', desc: 'Send/start WhatsApp campaigns' },
    ],
  },
  {
    label: 'Email Campaigns',
    scopes: [
      { value: 'email_campaigns:read', desc: 'Read email campaign data and analytics' },
      { value: 'email_campaigns:write', desc: 'Create and update email campaigns' },
      { value: 'email_campaigns:execute', desc: 'Send/start email campaigns' },
    ],
  },
  {
    label: 'Transactional Messages',
    requiresWaba: true,
    scopes: [
      { value: 'messages:send', desc: 'Send transactional WhatsApp messages via API' },
      { value: 'messages:read', desc: 'Read message status and history' },
    ],
  },
  {
    label: 'Webhooks',
    scopes: [
      { value: 'webhooks:manage', desc: 'Create, update and delete webhook subscriptions' },
    ],
  },
];

const WEBHOOK_EVENTS = [
  { value: 'campaign.started', label: 'Campaign Started' },
  { value: 'campaign.completed', label: 'Campaign Completed' },
  { value: 'campaign.failed', label: 'Campaign Failed' },
  { value: 'contact.bounced', label: 'Contact Bounced' },
  { value: 'contact.unsubscribed', label: 'Contact Unsubscribed' },
];

// Build scope groups from API response or fallback
// Prefers the `groups` array returned by the backend (ordered, labelled).
// Falls back to prefix-grouping if `groups` is absent.
function buildScopeGroups(apiScopes, descriptions, apiGroups) {
  if (!apiScopes || !Array.isArray(apiScopes) || apiScopes.length === 0) {
    return FALLBACK_SCOPE_GROUPS;
  }
  // Use server-provided groups array when available
  if (Array.isArray(apiGroups) && apiGroups.length > 0) {
    const wabaLabels = ['WhatsApp Templates', 'WhatsApp Campaigns', 'Transactional Messages'];
    return apiGroups
      .filter((g) => Array.isArray(g.scopes) && g.scopes.length > 0)
      .map((g) => ({
        label: g.label,
        requiresWaba: wabaLabels.includes(g.label),
        scopes: g.scopes.map((s) => ({ value: s, desc: descriptions?.[s] || s })),
      }));
  }
  // Fallback: group by prefix
  const groupMap = {};
  const groupLabels = {
    contacts: 'Contacts & Addressbooks',
    templates: 'WhatsApp Templates',
    email_templates: 'Email Templates',
    campaigns: 'WhatsApp Campaigns',
    email_campaigns: 'Email Campaigns',
    messages: 'Transactional Messages',
    webhooks: 'Webhooks',
  };
  const wabaGroups = ['templates', 'campaigns', 'messages'];
  apiScopes.forEach((s) => {
    const prefix = s.includes(':') ? s.split(':')[0] : s;
    if (!groupMap[prefix]) {
      groupMap[prefix] = {
        label: groupLabels[prefix] || prefix,
        requiresWaba: wabaGroups.includes(prefix),
        scopes: [],
      };
    }
    groupMap[prefix].scopes.push({ value: s, desc: descriptions?.[s] || s });
  });
  return Object.values(groupMap);
}

export default function GenerateKeyModal({ open, onCancel, onSuccess, wabaReady = true }) {
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState([]);
  const [rateLimit, setRateLimit] = useState(60);
  const [expiresAt, setExpiresAt] = useState(null);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [allowedIps, setAllowedIps] = useState([]);
  const [ipInput, setIpInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Dynamic scopes from API
  const [scopeGroups, setScopeGroups] = useState(FALLBACK_SCOPE_GROUPS);
  const [scopesLoading, setScopesLoading] = useState(false);

  // Fetch scopes once when modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setScopesLoading(true);
      try {
        const res = await ApiKeyService.getScopes();
        if (!cancelled && res.success && res.entity) {
          const groups = buildScopeGroups(
            res.entity.scopes,
            res.entity.description,
            res.entity.groups,
          );
          if (groups.length > 0) setScopeGroups(groups);
        }
      } catch {
        // fallback already set
      } finally {
        if (!cancelled) setScopesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const handleScopeToggle = (scope, checked) => {
    setSelectedScopes((prev) =>
      checked ? [...prev, scope] : prev.filter((s) => s !== scope)
    );
  };

  const handleGroupSelectAll = (group, checked) => {
    const vals = group.scopes.map((s) => s.value);
    setSelectedScopes((prev) => {
      const without = prev.filter((s) => !vals.includes(s));
      return checked ? [...without, ...vals] : without;
    });
  };

  const isGroupAllSelected = (g) => g.scopes.every((s) => selectedScopes.includes(s.value));
  const isGroupPartial = (g) => g.scopes.some((s) => selectedScopes.includes(s.value)) && !isGroupAllSelected(g);

  const handleGenerate = async () => {
    if (!keyName.trim()) { message.warning('Please enter a key name'); return; }
    if (selectedScopes.length === 0) { message.warning('Select at least one permission'); return; }
    if (webhookEnabled && webhookUrl && webhookEvents.length === 0) {
      message.warning('Select at least one webhook event'); return;
    }

    const payload = {
      keyName: keyName.trim(),
      scopes: selectedScopes,
      rateLimitPerMinute: rateLimit || 60,
    };
    if (expiresAt) payload.expiresAt = expiresAt.toISOString();
    if (allowedIps.length > 0) payload.allowedIps = allowedIps;
    if (webhookEnabled && webhookUrl.trim()) {
      payload.webhookUrl = webhookUrl.trim();
      payload.webhookEvents = webhookEvents;
    }

    setLoading(true);
    try {
      const result = await ApiKeyService.generateKey(payload);
      if (result.success && result.entity) {
        onSuccess(result);
        resetForm();
      } else {
        message.error(typeof result.error === 'string' ? result.error : 'Failed to generate key');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error;
      message.error(typeof msg === 'string' ? msg : 'Failed to generate API key');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setKeyName('');
    setSelectedScopes([]);
    setRateLimit(60);
    setExpiresAt(null);
    setWebhookEnabled(false);
    setWebhookUrl('');
    setWebhookEvents([]);
    setAllowedIps([]);
    setIpInput('');
  };

  const handleClose = () => { resetForm(); onCancel(); };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      title={<span className="api-key-modal-title"><KeyOutlined /> Generate New API Key</span>}
      footer={null}
      width={580}
      destroyOnClose
      className="generate-key-modal"
    >
      {/* Key Name */}
      <div className="api-key-form-section">
        <Text strong className="api-key-label">Key Name *</Text>
        <Input placeholder="e.g., Production CRM Integration" value={keyName} onChange={(e) => setKeyName(e.target.value)} maxLength={60} size="large" />
      </div>

      {/* Rate Limit + Expiry row */}
      <div className="api-key-form-grid api-key-form-section">
        <div className="api-key-form-col">
          <Text strong className="api-key-label">
            Rate Limit (req/min)
            <Tooltip title="Max requests per minute for this key. Default 60."><InfoCircleOutlined style={{ marginLeft: 4, color: '#8c8c8c' }} /></Tooltip>
          </Text>
          <InputNumber min={10} max={1000} value={rateLimit} onChange={setRateLimit} style={{ width: '100%' }} />
        </div>
        <div className="api-key-form-col">
          <Text strong className="api-key-label">Expiry Date (optional)</Text>
          <DatePicker
            style={{ width: '100%' }}
            value={expiresAt}
            onChange={setExpiresAt}
            disabledDate={(d) => d && d.isBefore(new Date(), 'day')}
            placeholder="Never expires"
            allowClear
          />
        </div>
      </div>

      {/* Permissions */}
      <Text strong className="api-key-label api-key-label--section">Permissions *</Text>
      {scopesLoading ? (
        <div className="api-key-loading"><Spin size="small" /> Loading scopes...</div>
      ) : (
        scopeGroups.map((group) => (
          <div key={group.label} className="scope-group">
            <div className="scope-group-header">
              <h4>
                {group.label}
                {group.requiresWaba && !wabaReady && (
                  <Tooltip title="WhatsApp scopes won't work until you complete embedded signup.">
                    <WarningOutlined style={{ color: '#faad14', marginLeft: 6, fontSize: 13 }} />
                  </Tooltip>
                )}
              </h4>
              <Checkbox
                checked={isGroupAllSelected(group)}
                indeterminate={isGroupPartial(group)}
                onChange={(e) => handleGroupSelectAll(group, e.target.checked)}
                className="scope-group-select-all"
              >
                <Text type="secondary" style={{ fontSize: 12 }}>All</Text>
              </Checkbox>
            </div>
            {group.scopes.map((scope) => (
              <Checkbox
                key={scope.value}
                checked={selectedScopes.includes(scope.value)}
                onChange={(e) => handleScopeToggle(scope.value, e.target.checked)}
                className="scope-item-checkbox"
              >
                <span className="scope-item-desc">{scope.desc || scope.value}</span>
                <Text type="secondary" className="scope-item-code">({scope.value})</Text>
              </Checkbox>
            ))}
            {group.requiresWaba && !wabaReady && (
              <Alert type="warning" showIcon message="WhatsApp scopes require embedded signup to be completed." style={{ marginTop: 6, fontSize: 12 }} banner />
            )}
          </div>
        ))
      )}

      {/* IP Whitelist */}
      <Divider style={{ margin: '16px 0 8px' }} />
      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <SafetyOutlined style={{ color: '#52c41a' }} /> IP Whitelist (optional)
        </Text>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          Restrict API key usage to specific IP addresses. Leave empty to allow all IPs.
        </Text>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {allowedIps.map((ip) => (
            <Tag
              key={ip}
              closable
              onClose={() => setAllowedIps((prev) => prev.filter((i) => i !== ip))}
              style={{ fontFamily: 'monospace', fontSize: 12 }}
            >
              {ip}
            </Tag>
          ))}
          {allowedIps.length === 0 && (
            <Text type="secondary" italic style={{ fontSize: 12 }}>No IP restrictions — all IPs allowed</Text>
          )}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <Input
            placeholder="e.g. 203.0.113.5 or 10.0.0.0/24"
            value={ipInput}
            onChange={(e) => setIpInput(e.target.value)}
            onPressEnter={() => {
              const ip = ipInput.trim();
              if (!ip) return;
              const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
              if (!ipRegex.test(ip)) { message.warning('Enter a valid IPv4 address or CIDR block'); return; }
              if (allowedIps.includes(ip)) { message.info('IP already added'); return; }
              setAllowedIps((prev) => [...prev, ip]);
              setIpInput('');
            }}
            size="small"
            style={{ maxWidth: 280 }}
          />
          <Button
            size="small"
            icon={<PlusIcon />}
            onClick={() => {
              const ip = ipInput.trim();
              if (!ip) return;
              const ipRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
              if (!ipRegex.test(ip)) { message.warning('Enter a valid IPv4 address or CIDR block'); return; }
              if (allowedIps.includes(ip)) { message.info('IP already added'); return; }
              setAllowedIps((prev) => [...prev, ip]);
              setIpInput('');
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* Webhook Config (collapsed by default) */}
      <Divider style={{ margin: '16px 0 8px' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Switch size="small" checked={webhookEnabled} onChange={setWebhookEnabled} />
        <Text strong><GlobalOutlined /> Configure Webhook (optional)</Text>
      </div>
      {webhookEnabled && (
        <div style={{ paddingLeft: 4 }}>
          <div style={{ marginBottom: 10 }}>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Webhook URL</Text>
            <Input placeholder="https://yourserver.com/webhook" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />
          </div>
          <div>
            <Text style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Events</Text>
            {WEBHOOK_EVENTS.map((evt) => (
              <Checkbox
                key={evt.value}
                checked={webhookEvents.includes(evt.value)}
                onChange={(e) => {
                  setWebhookEvents((prev) =>
                    e.target.checked ? [...prev, evt.value] : prev.filter((v) => v !== evt.value)
                  );
                }}
                style={{ display: 'block', marginLeft: 0, marginBottom: 4 }}
              >
                {evt.label} <Text type="secondary" style={{ fontSize: 11 }}>({evt.value})</Text>
              </Checkbox>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{ marginTop: 20, textAlign: 'right', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button type="primary" onClick={handleGenerate} loading={loading} disabled={!keyName.trim() || selectedScopes.length === 0}>
          Generate Key
        </Button>
      </div>
    </Modal>
  );
}
